import type { QueryClient } from '@tanstack/react-query';
import { GET_MEDIA_QUERY_KEY } from '@/state/queries/media/use-get-media.ts';
import { GET_EVENT_FILTERED_SESSIONS_QUERY_KEY } from '@/state/queries/sessions/use-filtered-event-sessions.ts';
import { GET_EVENT_FLOWS_QUERY_KEY } from '@/state/queries/flows/use-get-event-flows.ts';
import { GET_FLOW_QUERY_KEY } from '@/state/queries/flows/use-get-flow.ts';
import { GET_SESSION_QUERY_KEY } from '@/state/queries/sessions/use-get-session.ts';
import {
  GET_USER_CONTRIBUTORS_QUERY_KEY,
  GET_CONTRIBUTOR_SUMMARY_QUERY_KEY,
} from '@/state/queries/contributors/user-contributors';
import { GET_EVENT_SESSIONS_QUERY_KEY } from '@/state/queries/sessions/use-event-sessions.ts';
import { QUOTA_BALANCES_QUERY_KEY } from '@/state/queries/entitlements/use-quota-balances.ts';
import type { FlowDto } from '@/types/dtos/flows/response/flow.dto.ts';
import type { SessionDto } from '@/types/dtos/sessions/session.dto.ts';
import type { MediaDto } from '@/types/dtos/media/media.dto.ts';
import { getObjectId } from '@/helpers/object.id.helper';
import { invalidateSimulationCueSheet } from '@/state/queries/player/use-get-simulation-cue-sheet';

export type MediaParentRef = {
  parentId: string;
  parentType: string;
};

function mediaListContainsId(mediaList: MediaDto[] | undefined, mediaId: string): boolean {
  return Boolean(mediaList?.some((media) => media.id === mediaId));
}

function sessionContainsMedia(session: SessionDto, mediaId: string): boolean {
  if (mediaListContainsId(session.media, mediaId)) return true;
  return Boolean(
    session.mediaPlaylists?.session?.refs?.some((ref) => ref.mediaId === mediaId || ref.media?.id === mediaId),
  );
}

function findParentInSessionData(data: unknown, mediaId: string): MediaParentRef | undefined {
  const visit = (session: SessionDto): MediaParentRef | undefined => {
    if (!sessionContainsMedia(session, mediaId)) return undefined;
    const parentId = session.origin ? getObjectId(session.origin) : session.id;
    if (!parentId) return undefined;
    return { parentId, parentType: 'Session' };
  };

  if (!data || typeof data !== 'object') return undefined;

  if ('pages' in data && Array.isArray((data as { pages: unknown[] }).pages)) {
    for (const page of (data as { pages: unknown[] }).pages) {
      if (!page || typeof page !== 'object') continue;
      if (Array.isArray(page)) {
        for (const entry of page) {
          const nested = findParentInSessionData(entry, mediaId);
          if (nested) return nested;
        }
        continue;
      }
      if ('data' in page && Array.isArray((page as { data: SessionDto[] }).data)) {
        for (const session of (page as { data: SessionDto[] }).data) {
          const found = visit(session);
          if (found) return found;
        }
      }
    }
    return undefined;
  }

  if (Array.isArray(data)) {
    for (const entry of data) {
      if (entry && typeof entry === 'object' && 'id' in entry && 'media' in entry) {
        const found = visit(entry as SessionDto);
        if (found) return found;
      }
    }
  } else if ('id' in data && 'media' in data) {
    return visit(data as SessionDto);
  }

  return undefined;
}

function findParentInFlowData(data: unknown, mediaId: string): MediaParentRef | undefined {
  const flows = Array.isArray(data) ? data : data ? [data] : [];
  for (const flow of flows) {
    if (!flow || typeof flow !== 'object' || !('id' in flow)) continue;
    const flowDto = flow as FlowDto;
    if (mediaListContainsId(flowDto.media, mediaId)) {
      const parentId = flowDto.origin ? getObjectId(flowDto.origin) : flowDto.id;
      if (parentId) return { parentId, parentType: 'Flow' };
    }
    for (const slot of flowDto.slots ?? []) {
      if (slot.contentType !== 'Session' || typeof slot.content !== 'object' || !slot.content) continue;
      if (sessionContainsMedia(slot.content as SessionDto, mediaId)) {
        const session = slot.content as SessionDto;
        const parentId = session.origin ? getObjectId(session.origin) : session.id;
        if (parentId) return { parentId, parentType: 'Session' };
      }
    }
  }
  return undefined;
}

/** Resolve media parent from any cached media / session / flow query (session playlist media often skips GET_MEDIA). */
export function resolveMediaParentFromCache(queryClient: QueryClient, mediaId: string): MediaParentRef | undefined {
  const mediaCache = queryClient.getQueriesData({ queryKey: [GET_MEDIA_QUERY_KEY] });
  for (const [, mediaData] of mediaCache) {
    if (mediaData && typeof mediaData === 'object' && 'pages' in mediaData) {
      const pages = Array.isArray((mediaData as { pages?: unknown[] }).pages)
        ? (mediaData as { pages: unknown[] }).pages
        : [];
      for (const page of pages) {
        if (page && typeof page === 'object' && 'data' in page) {
          const pageData = (page as { data: unknown }).data;
          const media = Array.isArray(pageData) ? pageData : [pageData];
          const foundMedia = media.find(
            (m): m is { id: string; parent?: { id: string }; parentType?: string } =>
              m && typeof m === 'object' && 'id' in m && m.id === mediaId,
          );
          if (foundMedia?.parent?.id && foundMedia?.parentType) {
            return { parentId: foundMedia.parent.id, parentType: foundMedia.parentType };
          }
        }
      }
    }
  }

  for (const key of [GET_SESSION_QUERY_KEY, GET_EVENT_SESSIONS_QUERY_KEY, GET_EVENT_FILTERED_SESSIONS_QUERY_KEY]) {
    for (const [, data] of queryClient.getQueriesData({ queryKey: [key] })) {
      const found = findParentInSessionData(data, mediaId);
      if (found) return found;
    }
  }

  for (const key of [GET_FLOW_QUERY_KEY, GET_EVENT_FLOWS_QUERY_KEY]) {
    for (const [, data] of queryClient.getQueriesData({ queryKey: [key] })) {
      const found = findParentInFlowData(data, mediaId);
      if (found) return found;
    }
  }

  return undefined;
}

export function invalidateMediaUploadQueries(queryClient: QueryClient, mediaId: string) {
  queryClient.invalidateQueries({ queryKey: [GET_MEDIA_QUERY_KEY] });
  queryClient.invalidateQueries({ queryKey: [GET_EVENT_FILTERED_SESSIONS_QUERY_KEY] });
  queryClient.invalidateQueries({ queryKey: [GET_EVENT_FLOWS_QUERY_KEY] });
  queryClient.invalidateQueries({ queryKey: [GET_USER_CONTRIBUTORS_QUERY_KEY] });
  queryClient.invalidateQueries({ queryKey: [GET_CONTRIBUTOR_SUMMARY_QUERY_KEY] });
  queryClient.invalidateQueries({ queryKey: [QUOTA_BALANCES_QUERY_KEY] });
  void invalidateSimulationCueSheet(queryClient);

  const parent = resolveMediaParentFromCache(queryClient, mediaId);
  const parentId = parent?.parentId;
  const parentTypeLower = parent?.parentType?.toLowerCase();

  if (parentTypeLower === 'session' && parentId) {
    queryClient.invalidateQueries({ queryKey: [GET_SESSION_QUERY_KEY, parentId] });
    queryClient.refetchQueries({ queryKey: [GET_SESSION_QUERY_KEY, parentId], type: 'active' });
    queryClient.invalidateQueries({ queryKey: [GET_EVENT_SESSIONS_QUERY_KEY] });
    queryClient.refetchQueries({ queryKey: [GET_EVENT_SESSIONS_QUERY_KEY], type: 'active' });
    queryClient.refetchQueries({ queryKey: [GET_EVENT_FILTERED_SESSIONS_QUERY_KEY], type: 'active' });
  } else if (!parentId) {
    queryClient.invalidateQueries({ queryKey: [GET_SESSION_QUERY_KEY] });
    queryClient.invalidateQueries({ queryKey: [GET_EVENT_SESSIONS_QUERY_KEY] });
    queryClient.refetchQueries({ queryKey: [GET_SESSION_QUERY_KEY], type: 'active' });
    queryClient.refetchQueries({ queryKey: [GET_EVENT_SESSIONS_QUERY_KEY], type: 'active' });
    queryClient.refetchQueries({ queryKey: [GET_EVENT_FILTERED_SESSIONS_QUERY_KEY], type: 'active' });
  }

  if (parentTypeLower === 'flow' && parentId) {
    queryClient.invalidateQueries({ queryKey: [GET_FLOW_QUERY_KEY, parentId] });
    queryClient.refetchQueries({ queryKey: [GET_FLOW_QUERY_KEY, parentId], type: 'active' });
  } else if (parentTypeLower === 'session' && parentId) {
    const sessionId = parentId;
    const flowQueries = queryClient.getQueriesData({ queryKey: [GET_FLOW_QUERY_KEY] });
    const flowsToInvalidate = new Set<string>();

    for (const [queryKey, flowData] of flowQueries) {
      const flow = Array.isArray(flowData) ? flowData[0] : flowData;
      if (flow && typeof flow === 'object' && 'slots' in flow) {
        const flowDto = flow as FlowDto;
        const hasSession = flowDto.slots?.some((slot) => {
          if (slot.contentType !== 'Session') return false;
          const slotSessionId =
            typeof slot.content === 'string'
              ? slot.content
              : slot.content?.origin
                ? getObjectId(slot.content.origin)
                : slot.content?.id;
          return slotSessionId === sessionId;
        });
        if (hasSession) {
          const flowId =
            Array.isArray(queryKey) && queryKey.length > 1
              ? String(queryKey[1])
              : flowDto.id || (flowDto.origin ? getObjectId(flowDto.origin) : undefined);
          if (flowId) flowsToInvalidate.add(flowId);
        }
      }
    }

    const eventFlowsQueries = queryClient.getQueriesData({ queryKey: [GET_EVENT_FLOWS_QUERY_KEY] });
    for (const [, flowsData] of eventFlowsQueries) {
      const flows = Array.isArray(flowsData) ? flowsData : [];
      for (const flow of flows) {
        if (flow && typeof flow === 'object' && 'slots' in flow) {
          const flowDto = flow as FlowDto;
          const hasSession = flowDto.slots?.some((slot) => {
            if (slot.contentType !== 'Session') return false;
            const slotSessionId =
              typeof slot.content === 'string'
                ? slot.content
                : slot.content?.origin
                  ? getObjectId(slot.content.origin)
                  : slot.content?.id;
            return slotSessionId === sessionId;
          });
          if (hasSession) {
            const flowId = flowDto.id || (flowDto.origin ? getObjectId(flowDto.origin) : undefined);
            if (flowId) flowsToInvalidate.add(flowId);
          }
        }
      }
    }

    flowsToInvalidate.forEach((flowId) => {
      queryClient.invalidateQueries({ queryKey: [GET_FLOW_QUERY_KEY, flowId] });
      queryClient.refetchQueries({ queryKey: [GET_FLOW_QUERY_KEY, flowId], type: 'active' });
    });

    if (flowsToInvalidate.size === 0) {
      queryClient.invalidateQueries({ queryKey: [GET_FLOW_QUERY_KEY] });
      queryClient.refetchQueries({ queryKey: [GET_FLOW_QUERY_KEY], type: 'active' });
    }
  } else {
    queryClient.invalidateQueries({ queryKey: [GET_FLOW_QUERY_KEY] });
    queryClient.refetchQueries({ queryKey: [GET_FLOW_QUERY_KEY], type: 'active' });
  }
}
